import { useEffect, useState } from "react";
import { api } from "../lib/api";

/**
 * Aggregated Error Rate — Coffin Nail.
 * Shows Warden-flagged error rate: all-time · 7d, bold, next to the cost meter.
 * Refreshes on mount, on `refreshKey` change (parent bumps after each turn),
 * and on a 30s idle interval.
 */
export default function ErrorRatePill({ refreshKey = 0 }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const d = await api.errorRate();
        if (!cancelled) { setData(d); setErr(false); }
      } catch {
        if (!cancelled) setErr(true);
      }
    };
    load();
    const iv = setInterval(load, 30000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [refreshKey]);

  const fmt = (r) => `${(r * 100).toFixed(1)}%`;
  const allTime = data?.all_time;
  const sevenDay = data?.seven_day;
  const displayAll = allTime && allTime.reviews > 0 ? fmt(allTime.rate) : "—";
  const displaySeven = sevenDay && sevenDay.reviews > 0 ? fmt(sevenDay.rate) : "—";

  return (
    <div
      data-testid="error-rate-pill"
      title={
        data
          ? `Warden aggregate error rate.\n` +
            `All-time: ${allTime.errors}/${allTime.reviews} → ${fmt(allTime.rate)}\n` +
            `7-day:    ${sevenDay.errors}/${sevenDay.reviews} → ${fmt(sevenDay.rate)}\n\n` +
            `${data.definition}`
          : "Warden aggregate error rate — loading."
      }
      className="border border-sage-border bg-sage-elevated px-3 py-2 font-mono tabular-nums text-sm inline-flex items-center gap-3"
    >
      <span className="text-xs uppercase tracking-[0.1em] text-sage-muted">Err</span>
      {err || !data ? (
        <span className="text-sage-faint" data-testid="error-rate-pill-loading">—</span>
      ) : (
        <>
          <span
            className="font-bold text-sage-text"
            data-testid="error-rate-pill-alltime"
          >
            {displayAll}
          </span>
          <span className="text-xs text-sage-faint">·</span>
          <span
            className="font-bold text-sage-warn"
            data-testid="error-rate-pill-7d"
          >
            7d {displaySeven}
          </span>
        </>
      )}
    </div>
  );
}
